import React from "react";
import { companyInfo } from "@data/companyInfo";
import { teamMembers } from "@data/teamMembers";
import SEO from "@components/SEO";
import {
  createBusinessStructuredData,
  createBreadcrumbStructuredData,
} from "../utils/structuredData";

const AboutPage: React.FC = () => {
  const displayTeamMembers = teamMembers.filter(
    (member) => member.displayOnAbout,
  );

  const TeamMemberCard: React.FC<{ member: (typeof teamMembers)[0] }> = ({
    member,
  }) => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
      {/* Profile Image */}
      {/* <div className="relative h-64 overflow-hidden">
        <img
          src={member.profileImage}
          alt={member.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div> */}

      {/* Member Info */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-primaryText mb-1 group-hover:text-primary transition-colors">
          {member.name}
        </h3>
        <p className="text-primary font-semibold mb-3">{member.position}</p>

        <p className="text-gray-600 mb-4 text-sm leading-relaxed">
          {member.bio}
        </p>

        {/* Contact Info */}
        <div className="space-y-2 text-sm">
          {member.email && (
            <div className="flex items-center text-primaryText">
              <svg
                className="w-4 h-4 mr-2 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <a
                href={`mailto:${member.email}`}
                className="hover:text-primary transition-colors"
              >
                {member.email}
              </a>
            </div>
          )}
          {member.phone && (
            <div className="flex items-center text-primaryText">
              <svg
                className="w-4 h-4 mr-2 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <a
                href={`tel:${member.phone}`}
                className="hover:text-primary transition-colors"
              >
                {member.phone}
              </a>
            </div>
          )}
        </div>

        {/* WhatsApp Button */}
        <button
          onClick={() =>
            window.open(
              `https://wa.me/${member.whatsapp.replace("+", "")}?text=Hi ${
                member.name.split(" ")[0]
              }! I'm interested in learning more about your tours.`,
              "_blank",
            )
          }
          className="mt-4 w-full bg-whatsapp cursor-pointer text-white px-4 py-2 rounded-md font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
          <span>WhatsApp {member.name.split(" ")[0]}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <SEO
        title="About Ikhlas Tours | NZ Travel Company since 2013"
        description="Ikhlas Tours is a family-run, Auckland-based NZ travel company established in 2013, blending Kiwi travel style with Malaysian Muslim-friendly hospitality."
        path="/about"
        structuredData={[
          createBusinessStructuredData(companyInfo),
          createBreadcrumbStructuredData([
            { name: "Home", url: "/" },
            { name: "About", url: "/about" },
          ]),
        ]}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            About us
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            Your trusted partner for unforgettable New Zealand adventures
          </p>
        </div>
      </section>

      {/* Company Summary */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {companyInfo.aboutSummary}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">
                  Our Mission
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {companyInfo.mission}
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary mb-4">
                  Our History
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {companyInfo.history}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">Our Values</h2>
            <p className="text-lg text-primaryText max-w-3xl mx-auto">
              The values that drive our service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Sincerity
              </h3>
              <p className="text-gray-600 text-sm">
                Committed to honesty and integrity in every aspect of our
                business
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Quality
              </h3>
              <p className="text-gray-600 text-sm">
                Excellence in every aspect of your travel experience
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Guidance
              </h3>
              <p className="text-gray-600 text-sm">
                Guiding you through the best of New Zealand
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Innovation
              </h3>
              <p className="text-gray-600 text-sm">
                Creating unique and memorable travel experiences
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The passionate people who make your New Zealand dreams come true
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayTeamMembers.map((member) => (
              <TeamMemberCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">Our Impact</h2>
            <p className="text-lg text-primaryText max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">15+</div>
              <p className="text-primaryText">Years Experience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">
                10,000+
              </div>
              <p className="text-primaryText">Happy Travelers</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">50+</div>
              <p className="text-primaryText">Unique Tours</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">98%</div>
              <p className="text-primaryText">Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Ready to Experience New Zealand with Us?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join the many travelers who have discovered the magic of New Zealand
            with Ikhlas Tours
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/tours"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Explore Our Tours
            </a>
            <a
              href="/contact"
              className="inline-block border-2 border-primary bg-white text-primary px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
